import Stack from '@mui/material/Stack';

import { ensureDefined, invariant } from '@e-socna/utils';
import { ThirdPartyTypeEnum, getFragmentData } from '@e-socna/data-layer';

import { useThirdPartiesQuery } from '../../../../components/ThirdParties/hooks/useThirdPartiesQuery.hook';
import { NoData } from '../../../../components/Illustrations/NoData';
import { Loading } from '../../../../components/Loading';
import { ThirdPartiesCmp_Organization_Fragment } from '../../../../components/ThirdParties/fragments/organization.fragment';

import type { ThirdPartyForm_Person } from '../../fragments/person.fragment';

import { MembershipDetails } from '../MembershipDetails';

interface Props {
  readonly activeMemberships: ThirdPartyForm_Person['activeMemberships'];
  readonly isInternal: boolean;
}

export const MembershipsDetails = ({
  activeMemberships,
  isInternal,
}: Readonly<Props>) => {
  const { data, isSuccess } = useThirdPartiesQuery({
    isInternal,
    kind: ThirdPartyTypeEnum.Organization,
  });

  if (!activeMemberships.length) {
    return <NoData />;
  }

  if (!isSuccess) {
    return <Loading />;
  }

  invariant(
    !!data && 'findSomeOrganizations' in data,
    'findSomeOrganizations must be defined',
  );

  const organizations = getFragmentData(
    ThirdPartiesCmp_Organization_Fragment,
    data.findSomeOrganizations,
  );

  return (
    <Stack spacing={2} sx={{ width: '100%' }}>
      {activeMemberships.map((activeMembership, index) => {
        const { organization } = ensureDefined({
          organization: organizations.find(
            (organization) =>
              organization._id === activeMembership.organizationId,
          ),
        });

        return (
          <MembershipDetails
            index={index}
            isInternal={isInternal}
            key={activeMembership.organizationId}
            kind={ThirdPartyTypeEnum.Person}
            organization={organization}
            personMembership={activeMembership}
          />
        );
      })}
    </Stack>
  );
};
